import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './Snackbar.module.css';

/**
 * A lightweight toast/snackbar. Renders a floating message anchored to a corner
 * of the viewport and auto-hides after `autoHideDuration` ms.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Whether the snackbar is visible.
 * @param {import('react').ReactNode} [props.message] - The message to display (ignored if children are provided).
 * @param {import('react').ReactNode} [props.children] - Custom content rendered in place of `message`.
 * @param {number|null} [props.autoHideDuration=null] - Auto-dismiss delay in ms. Pass null to disable.
 * @param {(event: null, reason: 'timeout') => void} [props.onClose] - Called when the auto-hide timer elapses.
 * @param {{ vertical: 'top'|'bottom', horizontal: 'left'|'center'|'right' }} [props.anchorOrigin] - Corner to anchor to.
 * @param {'success'|'error'|'info'} [props.severity] - Colour variant. Omit for neutral dark.
 * @param {string} [props.className] - Optional class applied to the content surface.
 */
const Snackbar = ({
  open = false,
  message,
  children,
  autoHideDuration = null,
  onClose,
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  severity,
  className,
  ...rest
}) => {
  useEffect(() => {
    if (!open || autoHideDuration == null) return undefined;
    const timer = setTimeout(() => onClose?.(null, 'timeout'), autoHideDuration);
    return () => clearTimeout(timer);
  }, [open, autoHideDuration, onClose]);

  if (!open) return null;

  const verticalClasses = { top: styles.snackbarTop, bottom: styles.snackbarBottom };
  const horizontalClasses = {
    left: styles.snackbarLeft,
    center: styles.snackbarCenter,
    right: styles.snackbarRight,
  };
  const positionClass = [
    verticalClasses[anchorOrigin?.vertical] || styles.snackbarBottom,
    horizontalClasses[anchorOrigin?.horizontal] || styles.snackbarLeft,
  ].join(' ');

  const severityClass = severity ? styles[severity] : undefined;
  const icons = { success: '✓', error: '✕', info: 'ℹ' };

  const node = (
    <div className={[styles.snackbar, positionClass].join(' ')} {...rest}>
      <div
        className={[styles.snackbarContent, severityClass, className].filter(Boolean).join(' ')}
        role="alert"
      >
        {severity && <span className={styles.icon}>{icons[severity]}</span>}
        {children ?? <span className={styles.snackbarMessage}>{message}</span>}
      </div>
    </div>
  );

  // A <dialog> shown via showModal() (as BaseDialog does) is promoted to the
  // browser's top layer, which paints above every normal-layer element no
  // matter its z-index. ToastProvider renders this at the app root, so without
  // portaling the toast would sit *behind* any open modal. Target the last
  // open dialog in document order — with nested dialogs the inner one is
  // rendered later, and that's the one on top.
  const openDialogs =
    typeof document !== 'undefined' ? document.querySelectorAll('dialog[open]') : [];
  const dialogTarget = openDialogs.length ? openDialogs[openDialogs.length - 1] : null;

  return dialogTarget ? createPortal(node, dialogTarget) : node;
};

export default Snackbar;
